import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
// import { useSignIn, useClerk } from "@clerk/clerk-react";
import { loginUser } from "../../store/actions/userActions";
import ReCAPTCHA from "react-google-recaptcha";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  updateSession,
  clearBootstrappedSessions,
} from "../../store/actions/sessionsActions";
import NotificationCard from "../popover/NotificationCard";
import {
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Link,
  TextField,
  Typography,
  Grid2 as Grid,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
  Modal,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { Visibility, VisibilityOffOutlined } from "@mui/icons-material";

const LoginComponent = () => {
  // const { signIn } = useSignIn();
  // const { signOut } = useClerk();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [defEmail, setDefEmail] = useState("");
  const [defPwd, setDefPwd] = useState("");
  const [remember, setRemember] = useState(false);
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const miniSize = useMediaQuery("(max-width:330px)");
  const [recaptchaValue, setRecaptchaValue] = useState("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [count, setCount] = useState(0);
  const [recaptchaKey, setRecaptchaKey] = useState(Date.now());
  const [captchaModal, setCaptchaModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberInStorage, setRememberInStorage] = useState(false);
  const [navigateLoading, setNavigateLoading] = useState(false);
  const handleRecaptchaVerify = async (response) => {
    setRecaptchaValue(response);
    setRecaptchaVerified(true);
  };
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("Your session has expired!");

  const loginType = "Contractor";

  useEffect(() => {
    const expiredSession = sessionStorage.getItem("expiredSession");
    const loginAlertMessage = sessionStorage.getItem("loginAlertMessage");

    if (loginAlertMessage && !openAlert) {
      setAlertMessage(loginAlertMessage);
      setOpenAlert(true);
      sessionStorage.removeItem("loginAlertMessage");
      return;
    }

    if (expiredSession && !openAlert) {
      setAlertMessage("Your session has expired!");
      setOpenAlert(true);
      sessionStorage.removeItem("expiredSession");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemember = (e, formik) => {
    const isChecked = e.target.checked;

    if (!isChecked) {
      // Unchecking: Clear ALL remember me data immediately
      localStorage.removeItem("ujsffq");
      setDefEmail("");
      setDefPwd("");
      setRememberInStorage(false);
      // Don't clear the form - user might have typed new credentials
    }

    setRemember(isChecked);
  };

  const resetRecaptcha = () => {
    setRecaptchaKey(Date.now());
    setRecaptchaValue("");
    setRecaptchaVerified(false);
    if (count > 1) {
      setLoginDisabled(true);
    }
  };

  useEffect(() => {
    const isRecaptcha = localStorage.getItem("recaptcha") === "true";
    const RecaptchaAttempt = localStorage.getItem("failedAttempts") === "2";
    if (count > 1 || isRecaptcha || RecaptchaAttempt) {
      if (captchaModal === false) {
        setCaptchaModal(true);
        localStorage.setItem("recaptcha", "true");
        localStorage.setItem("failedAttempts", "2");
      }
      if (recaptchaVerified) {
        const interval = setInterval(() => {
          localStorage.removeItem("recaptcha");
          localStorage.removeItem("failedAttempts");
          setCount(0);
          setCaptchaModal(false);
        }, 2000);

        return () => clearInterval(interval);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, mobileSize, recaptchaVerified]);

  const signInButtonStyle = mobileSize
    ? {
        width: "15%",
        backgroundColor: "#F8FAFC",
        mb: 1,
        p: "6px",
      }
    : {
        width: "145px",
        backgroundColor: "#F8FAFC",
        py: "5px",
      };

  useEffect(() => {
    const getItemFromLocalStorage = (key) => {
      const item = localStorage.getItem(key);
      return JSON.parse(item);
    };

    const userData = getItemFromLocalStorage("ujsffq");

    if (userData) {
      setDefEmail(userData.email);
      setDefPwd("000000");
      setRemember(true);
      setRememberInStorage(true);
    }
  }, []);

  useEffect(() => {
    const isRecaptcha = localStorage.getItem("recaptcha") === "true";
    if (count > 1 || isRecaptcha) {
      if (recaptchaVerified === false) {
        setLoginDisabled(true);
      } else {
        setLoginDisabled(false);
      }
    }
  }, [recaptchaVerified, count]);

  useEffect(() => {
    if (
      error !== "" &&
      error !== "We are updating our application. Please come back later"
    ) {
      const timer = setTimeout(() => {
        setError("");
      }, 6000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [error]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: defEmail || "",
      password: defPwd || "",
      loginType: loginType,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Email address must be a valid email")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
      loginType: Yup.string().required("Login type is required"),
    }),
    onSubmit: async (values, helpers) => {
      // Clear any admin remote connection flags and saved grid state on login attempt
      try {
        sessionStorage.removeItem("adminRemote");
        sessionStorage.removeItem("adminRemoteEmail");
        sessionStorage.removeItem("adminRemoteSessionType");
        sessionStorage.removeItem("adminRemoteCustomersState");
      } catch (e) {
        // ignore storage errors
      }
      if (count > 1) {
        if (recaptchaVerified === false) {
          return;
        }
        // const is_verified = await handleVerifyRecaptcha();

        // if (is_verified.data.success === false) {
        //   return;
        // }
      }
      try {
        setLoading(true);
        const { email, password, loginType } = values;

        // Check if user is switching accounts (different email from stored)
        const storedData = localStorage.getItem("ujsffq");
        const storedEmail = storedData ? JSON.parse(storedData).email : null;
        const isSwitchingAccounts =
          storedEmail && storedEmail.toLowerCase() !== email.toLowerCase();

        // If switching accounts OR remember is unchecked, clear everything
        if (isSwitchingAccounts || !remember) {
          localStorage.removeItem("ujsffq");
          setDefEmail("");
          setDefPwd("");
          setRememberInStorage(false);
        }

        // If remember is unchecked, force rememberInStorage to false and oldEmail to empty
        // This ensures backend ignores any existing remember cookie
        const effectiveRememberInStorage = remember ? rememberInStorage : false;
        const effectiveOldEmail = remember ? defEmail : "";

        const result = await dispatch(
          loginUser(
            email,
            password,
            remember,
            recaptchaValue,
            count,
            effectiveOldEmail,
            effectiveRememberInStorage,
            loginType,
          ),
        );
        if (result?.success === false) {
          resetRecaptcha();
          setCount(count + 1);
          const RecaptchaAttempt = localStorage.getItem("failedAttempts");

          if (!RecaptchaAttempt) {
            localStorage.setItem("failedAttempts", "1");
          } else if (RecaptchaAttempt === "1") {
            localStorage.setItem("failedAttempts", "2");
          }
          setRemember(false);
          if (typeof localStorage !== "undefined") {
            // Remove the item with the name "ujsffq"
            localStorage.removeItem("ujsffq");
          }
          formik.setFieldValue("email", "");
          formik.setFieldValue("password", "");
          formik.setTouched({});
          setError(result?.message);
          setLoading(false);
          return;
        }
        if (result && result?.data?.message === "normal") {
          // Respect the current remember flag when persisting/removing remembered email
          if (remember) {
            localStorage.setItem("ujsffq", JSON.stringify({ email }));
          } else {
            localStorage.removeItem("ujsffq");
          }

          // if (result.data.first) {
          //   navigate("/selectcounties");
          // } else {
          // await dispatch(
          //   connectWebsocket(result?.data?.sessionType, result?.data?.token)
          // );

          // Clear bootstrapped sessions to force fresh data fetch for new/different user
          await dispatch(clearBootstrappedSessions());
          await dispatch(updateSession(result?.data?.data));

          if (result?.data?.role?.includes("formDigUser")) {
            navigate("/form-uploader");
          } else if (result?.data?.data?.sessionType === "notarySession") {
            navigate("/notary/dashboard");
          } else if (
            result?.data?.data?.sessionType === "userSession" ||
            result?.data?.data?.sessionType === "subUserSession" ||
            result?.data?.data?.sessionType === "adminSession" ||
            result?.data?.data?.sessionType === "privateProviderSession"
          ) {
            navigate("/dashboard");
          }
        } else if (result && result?.data?.message === "checkout") {
          window.location.href = result?.data?.data;
        } else if (result && result?.data?.message === "faunset") {
          const token = result?.data?.data;
          navigate(`/2fa-secure?token=${token}`);
          return;
        } else if (result && result?.data?.message === "emailFa") {
          const token = result?.data?.data;
          navigate(`/2fa-secure/email?token=${token}&remember=${remember}`);
          return;
        } else if (result && result?.data?.message === "totp") {
          const token = result?.data?.data;
          navigate(`/2fa-secure/totp?token=${token}&remember=${remember}`);
          return;
        } else if (result && result?.data?.message === "smsFa") {
          const token = result?.data?.data;
          navigate(`/2fa-secure/sms?token=${token}&remember=${remember}`);
          return;
        }
      } catch (err) {
        setLoading(false);
        setError(err.response.data.message);
        setCount(count + 1);
        resetRecaptcha();
      }
    },
  });

  const signInWith = async (strategy) => {
    try {
      // await signIn.authenticateWithRedirect({
      //   strategy,
      //   redirectUrl: "/login",
      //   redirectUrlComplete: "/sso_redirect_1saf32mf23mnk23f23",
      // });
    } catch (e) {}
  };

  return (
    <>
      <Box
        sx={
          mobileSize
            ? {
                justifyContent: "center",
                overflow: "auto",
                minHeight: "100vh",
                overflowY: "hidden",
                display: "flex",
                mx: 1,
                flexDirection: "column",
              }
            : {
                backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                backgroundRepeat: "repeat",
                minHeight: "100vh",
                overflow: "hidden",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }
        }
      >
        <NotificationCard
          open={openAlert}
          setOpen={setOpenAlert}
          message={alertMessage}
        />
        <Box
          sx={
            mobileSize
              ? {
                  backgroundColor: "#fff",
                  // py: "1rem",
                  width: "100%",
                  flexGrow: 1,
                }
              : {
                  backgroundColor: "#fff",
                  mt: 2,
                  maxWidth: 700,
                  borderRadius: "25px",
                  borderBottomLeftRadius: "0px",
                  borderBottomRightRadius: "0px",
                  px: 3,
                  py: "10px",
                  width: "100%",
                  pt: "0.5rem",
                }
          }
        >
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Box
              sx={{ cursor: "pointer", mt: "24px !important" }}
              display="flex"
              justifyContent="center"
              onClick={() => {
                navigate("/");
              }}
            >
              <img
                style={{
                  width: miniSize ? "8rem" : "9rem",
                  marginLeft: "auto",
                  marginRight: "auto",
                  marginTop: "-9px",
                }}
                alt="Permit Rockstar Logo"
                src="/img/logos/logo_main.png"
              />
            </Box>

            <Typography
              sx={{ textAlign: "center" }}
              color="#004976"
              variant="h6"
            >
              Hi, Welcome Back
            </Typography>
            <Typography
              sx={{ textAlign: "center", lineHeight: "3px", pb: "1rem" }}
              color="text.secondary"
              variant="body2"
            >
              Enter your credentials to continue
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                pt: 1,
                fontWeight: "normal",
                fontSize: "1rem",
              }}
              color="#101820"
              variant="h6"
            >
              {/* Sign in with */}
            </Typography>

            <Stack
              justifyContent="center"
              direction={mobileSize ? "row" : "row"}
              spacing={mobileSize ? 2 : 5}
              sx={{ mx: "auto" }}
            >
              {/* <Grid item xs={12} sm={4}> */}
              <Button
                disabled={true}
                size="large"
                sx={signInButtonStyle}
                variant="outlined"
                onClick={() => signInWith("oauth_google")}
              >
                <img
                  alt="google-sso"
                  src="/img/logos/google_logo.png"
                  style={{
                    width: "25px",
                    marginRight: mobileSize ? "0px" : "8px",
                  }}
                />
                {mobileSize ? "" : "Google"}
              </Button>
              {/* </Grid> */}
              {/* <Grid item xs={12} sm={4}> */}
              <Button
                disabled={true}
                size="large"
                sx={signInButtonStyle}
                variant="outlined"
                onClick={() => signInWith("oauth_facebook")}
              >
                <img
                  alt="facebook-sso"
                  src="/img/logos/facebook_logo.png"
                  style={{
                    width: "25px",
                    marginRight: mobileSize ? "0px" : "8px",
                  }}
                />
                {mobileSize ? "" : "Facebook"}
              </Button>
              {/* </Grid> */}
              {/* <Grid item xs={12} sm={4}> */}
              <Button
                disabled={true}
                size="large"
                sx={signInButtonStyle}
                variant="outlined"
                onClick={() => signInWith("oauth_apple")}
              >
                <img
                  alt="apple-sso"
                  src="/img/logos/apple_logo.png"
                  style={{ width: "35px" }}
                />
                {mobileSize ? "" : "Apple"}
              </Button>
              {/* </Grid> */}
            </Stack>
            <Typography
              sx={{
                textAlign: "center",
                pt: 0.5,
                fontWeight: "bold",
              }}
              variant="body2"
              color="primary"
            >
              {/* Sign in with */}
              Social logins are temporarily unavailable. Please try again later.
            </Typography>
            <Box
              sx={{
                flex: "1 1 auto",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                pt: "5px",
              }}
            >
              <div
                style={{
                  border: "1px solid #E7E5E5",
                  width: "100%",
                  marginRight: "2rem",
                }}
              />
              <Button
                disabled
                size="large"
                sx={{
                  width: "145px",
                  backgroundColor: "#F8FAFC",
                  py: "2px",
                  borderRadius: "8px",
                  mb: "10px",
                  mt: "8px",
                }}
                variant="outlined"
                // onClick={() => {
                //   signOut();
                // }}
              >
                OR
              </Button>
              <div
                style={{
                  border: "1px solid #E7E5E5",
                  width: "100%",
                  marginLeft: "2rem",
                }}
              />
            </Box>
            {/* <Typography
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                  color="primary"
                  variant="body1"
                >
                  Sign in with Email Address As
                </Typography> */}

            {/* Login Type Selection */}
            {/* <Box
                  sx={{
                    mt: 2,
                    mb: 1,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      size="small"
                      value={loginType}
                      onChange={(e) => {
                        setLoginType(e.target.value);
                        formik.setFieldValue("loginType", e.target.value);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="Contractor">Contractor</MenuItem>
                      <MenuItem value="Notary">Notary</MenuItem>
                    </Select>
                  </FormControl>
                </Box> */}
          </Stack>
        </Box>
        <Box
          sx={
            !mobileSize
              ? {
                  backgroundColor: "#fff",
                  mb: 2,
                  maxWidth: 700,
                  borderRadius: "25px",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                  px: 3,
                  py: "10px",
                  width: "100%",
                }
              : { mb: 3, backgroundColor: "#fff" }
          }
        >
          <form
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
            style={
              tabletSize && !mobileSize
                ? { paddingLeft: "0.5rem", paddingRight: "0.5rem" }
                : {}
            }
          >
            <Stack
              spacing={
                !!(formik.touched.email && formik.errors.email) &&
                !!(formik.touched.password && formik.errors.password)
                  ? 1
                  : 2
              }
              sx={{ px: mobileSize ? 0.2 : 0 }}
            >
              <TextField
                error={!!(formik.touched.email && formik.errors.email)}
                disabled={remember && rememberInStorage}
                fullWidth
                helperText={formik.touched.email && formik.errors.email}
                label="Email Address"
                name="email"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.handleChange(e); // Trigger Formik's handleChange as well
                }}
                type="email"
                value={formik.values.email}
              />
              <TextField
                error={!!(formik.touched.password && formik.errors.password)}
                type={showPassword ? "text" : "password"}
                fullWidth
                disabled={remember && rememberInStorage}
                helperText={formik.touched.password && formik.errors.password}
                label="Password"
                name="password"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={remember && rememberInStorage}
                        onClick={() => {
                          if (remember) {
                            return;
                          } else {
                            setShowPassword(!showPassword);
                          }
                        }}
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                        onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                        edge="end"
                      >
                        {showPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOffOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid size={{ xs: 6 }}>
                <Box>
                  <FormControlLabel
                    sx={{ mt: "5px", mr: "auto" }}
                    control={
                      <Checkbox
                        checked={remember}
                        onChange={(e) => handleRemember(e, formik)}
                      />
                    }
                    label={
                      <Typography
                        sx={{ textAlign: "center", lineHeight: "3px" }}
                        color="text.secondary"
                        variant="body2"
                      >
                        Remember Me
                      </Typography>
                    }
                    size="sm"
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Link
                    // component={NextLink}
                    sx={{
                      fontWeight: "bold",
                      ml: "auto",
                      mt: 1,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      navigate("/resetpassword");
                    }}
                    underline="hover"
                    variant="subtitle2"
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Grid>
            </Grid>

            {formik.errors.submit && (
              <Typography color="error" sx={{ mt: 3 }} variant="body2">
                {formik.errors.submit}
              </Typography>
            )}
            <Typography
              variant="body1"
              sx={{ minHeight: "1.5rem" }}
              textAlign="center"
              my={1}
              color="error"
              fontWeight="bold"
            >
              {error}
            </Typography>

            <LoadingButton
              disabled={loginDisabled}
              loading={loading}
              fullWidth
              size={mobileSize ? "medium" : "large"}
              sx={{ mt: 0, mb: mobileSize ? 1 : 0 }}
              type="submit"
              variant="contained"
            >
              Sign In
            </LoadingButton>
          </form>
          {!mobileSize && (
            <div
              style={{
                border: "1px solid #E7E5E5",
                width: "100%",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            />
          )}

          <Box
            sx={{
              flex: "1 1 auto",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              px: tabletSize && !mobileSize ? "0.5rem" : undefined,
            }}
          >
            <LoadingButton
              loading={navigateLoading}
              onClick={(e) => {
                setNavigateLoading(true);
                window.location.href = "https://permitrockstar.com/pricing/";
                // navigate("/register");
              }}
              color="warning"
              fullWidth
              size={mobileSize ? "small" : "large"}
              sx={{
                // backgroundColor: "##CDAF0B",

                ":hover": { backgroundColor: "#CDAF0B" },
              }}
              variant="contained"
            >
              Don't have an account? Register now for free
            </LoadingButton>

            <Link
              // component={NextLink}
              sx={{
                fontWeight: "bold",
                // ml: "auto",
                mt: 2,
                cursor: "pointer",
              }}
              onClick={(e) => {
                navigate("/");
              }}
              underline="hover"
              variant="subtitle2"
            >
              Back to Home
            </Link>
          </Box>
          {/* </div> */}
        </Box>
      </Box>
      <Modal open={captchaModal}>
        <Box
          sx={{
            position: "absolute",
            top: "0%",
            left: "0%",
            width: "100%",
            height: "100%",
            overflow: "auto", // Allows scrolling if the content exceeds the viewport
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
            display: "flex",
            alignItems: "center", // Center content vertically
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "4px",
            }}
          >
            <Typography variant="h5">Please Verify You Are Human</Typography>
            <Box mt={2}>
              <ReCAPTCHA
                key={recaptchaKey}
                sitekey={process.env.REACT_APP_RECAPTCHA}
                onChange={handleRecaptchaVerify}
              />
            </Box>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default LoginComponent;
